<template>
    <div class="vx-milestone-progress">
        <vx-progress
            :color="barColor"
            :opacity="progressOpacity"
            :height="height"
            :percent="timelinePercentage"
            class="progress-bar w-full"
            :class="{ 'no-end-date': !timelineEndDate }"
            :background-color="backgroundColor"
        >
            <div class="milestone-track" :style="{ width: `calc(100% - ${height}px)` }">
                <transition
                    v-for="(date, index) in sortedDatesWithPercentage"
                    :key="index"
                    appear
                    name="slide-fade-right"
                >
                    <div
                        position="bottom"
                        class="milestone-container"
                        :style="{
                            'margin-left': `calc(${date.percentage}% + 2px)`,
                            'transition-delay': `${index * 0.05}s`
                        }"
                    >
                        <slot
                            :date="date.date"
                            :objects="date.objects"
                            :percentage="date.percentage"
                            :index="index"
                        >
                            <div
                                :key="date.id"
                                class="milestone"
                                :style="{
                                    'height': `${height * 0.8}px`,
                                    'margin': `${height * 0.1}px`,
                                    'border': '2px solid red',
                                    'background-color': hasCustomMilestones
                                        ? 'transparent'
                                        : 'white'
                                }"
                            />
                        </slot>
                    </div>
                </transition>
            </div>
        </vx-progress>
        <div class="flex w-full justify-between con-text-validation">
            <div>
                <slot name="start-hint" :date="timelineStartDate" />
            </div>
            <div>
                <slot name="end-hint" :date="timelineEndDate" />
            </div>
        </div>
    </div>
</template>

<script>
import _color from '@/assets/utils/color.js'

export default {
    name: 'vx-milestone-progress',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        dateKey: {
            type: [Function, String],
            default: (o) => o
        },
        height: {
            type: [Number, String],
            default: 20
        },
        startDate: {
            type: [Date, String],
            required: true
        },
        endDate: {
            type: [Date, String],
            required: true
        },
        color: {
            type: String,
            default: 'success'
        },
        backgroundColor: {
            type: String
        },
        colorBreakpoints: {
            type: Object
            // default: () => {
            //     return {
            //         7: 'warning',
            //         14: 'danger'
            //     }
            // }
        },
        progressOpacity: {
            type: [Number, String],
            default: 1
        }
    },
    data() {
        return {
            currentDate: new Date(),
            dateUpdater: null
        }
    },
    computed: {
        getDate() {
            if (typeof this.dateKey == 'string') {
                return (o) => o[this.dateKey]
            }
            return this.dateKey
        },
        valueMap() {
            if (!this.value) {
                return {}
            }
            //check if value is purely date or object with date prop.
            return this.value.groupBy(
                function (o) {
                    var d = this.getDate(o)
                    var d2 = new Date(d)
                    var d3 = d2.toISODateString().replace(/-/g, '/')
                    return d3
                }.bind(this)
            )
        },
        sortedDateKeys() {
            return Object.keys(this.valueMap)
                .map((d) => new Date(d))
                .where((d) => d.isValid())
                .defaultSort()
                .map((d) => d.toISODateString().replace(/-/g, '/'))
        },
        sortedDatesWithPercentage() {
            return this.sortedDateKeys.map(
                function (d) {
                    var date = new Date(d)
                    return {
                        id: date.getTime(),
                        objects: this.valueMap[d],
                        date: date,
                        percentage: this.getPercentageFor(date)
                    }
                }.bind(this)
            )
        },
        timelineStartDate() {
            return new Date(this.startDate).getMidnight()
        },
        timelineEndDate() {
            return new Date(this.endDate).getMidnight()
        },
        hasCustomMilestones() {
            return this.hasSlot('default')
        },
        daysUntilEnd() {
            return this.timelineEndDate.daysUntil(this.currentDate)
        },
        breakpoints() {
            return Object.keys(this.colorBreakpoints)
                .map((v) => +v)
                .defaultSort()
        },
        barColor() {
            var color_x = this.color
            if (this.timelinePercentage == 100) {
                return color_x
            }

            if (this.colorBreakpoints && this.breakpoints.any()) {
                if (this.colorBreakpoints[this.daysUntilEnd]) {
                    return this.colorBreakpoints[this.daysUntilEnd]
                }
                var i = 0

                while (i < this.breakpoints.length) {
                    var currentBreakpoint = this.breakpoints[i]
                    var nextBreakpoint = this.breakpoints[i + 1]

                    if (!nextBreakpoint || nextBreakpoint > this.daysUntilEnd) {
                        color_x = this.colorBreakpoints[currentBreakpoint]
                        break
                    }
                    i++
                }
            }

            return color_x
        },
        totalDays() {
            // this COULD be 0, this may present an issue
            return this.timelineStartDate.daysUntil(this.timelineEndDate)
        },
        timelinePercentage() {
            if (!this.sortedDateKeys.any()) {
                return 100
            }
            return this.getPercentageFor(this.currentDate)
        }
    },
    methods: {
        hasSlot(slotName) {
            return !!(this.$slots[slotName] || this.$scopedSlots[slotName])
        },
        getPercentageFor(date) {
            // if it's the same day, show the end of that day.
            if (!this.totalDays) {
                return 100
            }
            var daysToDate = this.timelineStartDate.daysUntil(date.getMidnight(), true)
            return 100 * (daysToDate / this.totalDays)
        }
    },
    created() {
        this.dateUpdater = setInterval(
            function () {
                this.currentDate = new Date()
            }.bind(this),
            1000 * 60 // 1 minute
        )
    }
}
</script>

<style scoped lang="scss">
.milestone-track {
    display: flex;
    align-items: center;
    overflow: visible;
    position: relative;
    height: 100%;
    z-index: 100;
}
.milestone-container {
    position: absolute;
    overflow: visible;
    height: 100%;
    display: flex;
    align-items: center;
}
.progress-bar >>> .vs-progress--foreground {
    opacity: 0.5;
}
.progress-bar.no-end-date {
    // border-radius: 18px 0 0 18px;

    // &:after {
    //     background: linear-gradient(-135deg, transparent 16px, #1ba1e2 0),
    //         linear-gradient(-45deg, transparent 16px, #1ba1e2 0);
    //     background-repeat: repeat-y !important;
    //     background-position: left bottom !important;
    //     background-size: 32px 14px !important;
    //     content: '';
    //     display: block;
    //     height: 100%;
    //     position: relative;
    //     left: 100%;
    //     top: -30px;
    // }
}

.milestone {
    border-radius: 50%;
    aspect-ratio: 1;
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.8);
    }
}
</style>
